/**
 * ImportView - Archive Import Wizard
 *
 * Multi-step wizard for importing archives from various sources:
 * 1. Source Selection - Choose import type
 * 2. File Upload - Drag-drop or browse files/folders
 * 3. Preview - Show what will be imported
 * 4. Processing - Show progress and results
 *
 * Supports:
 * - Individual conversation JSON files
 * - OpenAI export folders (conversations.json)
 * - Browser plugin export folders (conversation.json + media/)
 * - Humanizer export format
 * - ChatGPT URL import
 *
 * @module @humanizer/studio/components/archive/ImportView
 */

import React, { useState, useCallback, useRef } from 'react';
import { useApi } from '../../contexts/ApiContext';
import { ElectronImportPanel } from '../import/ElectronImportPanel';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ImportViewProps {
  /** Called when import completes successfully */
  onImportComplete?: () => void;
  /** Optional class name */
  className?: string;
}

/** Import source types */
type ImportSource = 'chatgpt' | 'folder' | 'humanizer' | 'url';

/** Import wizard step */
type ImportStep = 'source' | 'upload' | 'preview' | 'processing' | 'results';

/** Parsed folder structure */
interface FolderParseResult {
  conversations: ParsedConversation[];
  mediaFiles: File[];
  totalSize: number;
}

/** A parsed conversation ready for import */
interface ParsedConversation {
  file: File;
  title: string;
  messageCount: number;
  source: string;
  createTime?: number;
}

/** Import result for a single conversation */
interface ConversationResult {
  title: string;
  threadId: string;
  messageCount: number;
  stored: number;
  skipped: number;
  error?: string;
}

/** Import results summary */
interface ImportResults {
  success: boolean;
  total: number;
  imported: number;
  failed: number;
  conversations: ConversationResult[];
}

// Source configuration
const SOURCES: Array<{
  id: ImportSource;
  name: string;
  description: string;
  icon: string;
  acceptTypes: string;
  allowFolder: boolean;
}> = [
  {
    id: 'chatgpt',
    name: 'ChatGPT Files',
    description: 'Import conversation JSON files',
    icon: '📄',
    acceptTypes: '.json',
    allowFolder: false,
  },
  {
    id: 'folder',
    name: 'Export Folder',
    description: 'Import a ChatGPT/Claude export folder with media',
    icon: '📁',
    acceptTypes: '.json',
    allowFolder: true,
  },
  {
    id: 'humanizer',
    name: 'Humanizer Export',
    description: 'Restore from a Humanizer export file',
    icon: '📦',
    acceptTypes: '.json',
    allowFolder: false,
  },
  {
    id: 'url',
    name: 'ChatGPT URL',
    description: 'Import directly from a ChatGPT conversation URL',
    icon: '🔗',
    acceptTypes: '',
    allowFolder: false,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Parse a conversation file to extract metadata for preview
 */
async function parseConversationFile(file: File): Promise<ParsedConversation | null> {
  try {
    const text = await file.text();
    const data = JSON.parse(text);

    // Detect format and extract metadata
    let title = 'Untitled';
    let messageCount = 0;
    let source = 'unknown';
    let createTime: number | undefined;

    if (data.mapping) {
      // OpenAI official format
      source = 'chatgpt';
      title = data.title || 'Untitled';
      messageCount = Object.values(data.mapping).filter(
        (n: any) => n.message?.content?.parts?.length > 0
      ).length;
      createTime = data.create_time;
    } else if (data.messages && data.source) {
      // Browser plugin format
      source = `plugin-${data.source.toLowerCase()}`;
      title = data.title || 'Untitled';
      if (typeof data.messages === 'object' && !Array.isArray(data.messages)) {
        // ChatGPT mapping style
        messageCount = Object.values(data.messages).filter(
          (n: any) => n.message?.content?.parts?.length > 0
        ).length;
      } else if (Array.isArray(data.messages)) {
        // Gemini/Claude array style
        messageCount = data.messages.length;
      }
    } else if (data.chat_messages) {
      // Claude native format
      source = 'claude';
      title = data.name || 'Untitled';
      messageCount = data.chat_messages.length;
      createTime = data.created_at ? new Date(data.created_at).getTime() / 1000 : undefined;
    } else if (Array.isArray(data)) {
      // OpenAI full export (array of conversations)
      source = 'chatgpt-export';
      title = `${data.length} conversations`;
      messageCount = data.reduce((sum, conv) => {
        const count = conv.mapping
          ? Object.values(conv.mapping).filter((n: any) => n.message?.content?.parts?.length > 0).length
          : 0;
        return sum + count;
      }, 0);
    }

    return {
      file,
      title,
      messageCount,
      source,
      createTime,
    };
  } catch {
    return null;
  }
}

/**
 * Recursively read files from a directory entry
 */
async function readDirectoryEntries(entry: FileSystemDirectoryEntry): Promise<File[]> {
  const files: File[] = [];
  const reader = entry.createReader();

  const readEntries = (): Promise<FileSystemEntry[]> =>
    new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });

  const getFile = (fileEntry: FileSystemFileEntry): Promise<File> =>
    new Promise((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });

  let entries: FileSystemEntry[];
  do {
    entries = await readEntries();
    for (const e of entries) {
      if (e.isFile) {
        const file = await getFile(e as FileSystemFileEntry);
        // Attach the relative path
        Object.defineProperty(file, 'webkitRelativePath', {
          value: e.fullPath.replace(/^\//, ''),
          writable: false,
        });
        files.push(file);
      } else if (e.isDirectory) {
        const subFiles = await readDirectoryEntries(e as FileSystemDirectoryEntry);
        files.push(...subFiles);
      }
    }
  } while (entries.length > 0);

  return files;
}

/**
 * Parse folder structure to find conversations and media
 */
async function parseFolderStructure(files: File[]): Promise<FolderParseResult> {
  const conversations: ParsedConversation[] = [];
  const mediaFiles: File[] = [];
  let totalSize = 0;

  // Group files by their directory
  const conversationFiles: File[] = [];

  for (const file of files) {
    totalSize += file.size;
    const path = file.webkitRelativePath || file.name;
    const pathLower = path.toLowerCase();

    // Check if it's a conversation JSON file
    if (pathLower.endsWith('conversation.json') || pathLower.endsWith('conversations.json')) {
      conversationFiles.push(file);
    } else if (pathLower.includes('/media/') || pathLower.startsWith('media/')) {
      // Media file
      mediaFiles.push(file);
    } else if (pathLower.endsWith('.json')) {
      // Other JSON files might be conversations too
      conversationFiles.push(file);
    } else if (/\.(png|jpg|jpeg|gif|webp|mp3|mp4|wav|ogg|webm)$/i.test(path)) {
      // Media file by extension
      mediaFiles.push(file);
    }
  }

  // Parse each conversation file
  for (const file of conversationFiles) {
    const parsed = await parseConversationFile(file);
    if (parsed) {
      conversations.push(parsed);
    }
  }

  return { conversations, mediaFiles, totalSize };
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function ImportView({
  onImportComplete,
  className = '',
}: ImportViewProps): React.ReactElement {
  const api = useApi();
  const isElectron = typeof window !== 'undefined' && !!window.isElectron;

  // All hooks must be called unconditionally (Rules of Hooks)
  const [currentStep, setCurrentStep] = useState<ImportStep>('source');
  const [selectedSource, setSelectedSource] = useState<ImportSource | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [folderParseResult, setFolderParseResult] = useState<FolderParseResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [results, setResults] = useState<ImportResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // ELECTRON: Import uses local filesystem via IPC
  if (isElectron) {
    return (
      <ElectronImportPanel
        onImportComplete={onImportComplete}
        className={className}
      />
    );
  }

  // Handle source selection
  const handleSourceSelect = useCallback((source: ImportSource) => {
    setSelectedSource(source);
    setSelectedFiles([]);
    setFolderParseResult(null);
    setResults(null);
    setError(null);
    setCurrentStep('upload');
  }, []);

  // Handle file selection from input
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setSelectedFiles(files);
    setError(null);

    // If folder import, parse the structure
    if (selectedSource === 'folder') {
      setIsParsing(true);
      try {
        const result = await parseFolderStructure(files);
        setFolderParseResult(result);
        if (result.conversations.length > 0) {
          setCurrentStep('preview');
        } else {
          setError('No conversation files found in the selected folder');
        }
      } catch (err) {
        setError('Failed to parse folder structure');
      } finally {
        setIsParsing(false);
      }
    }
  }, [selectedSource]);

  // Handle drag events
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  // Handle drop - supports both files and folders
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const items = Array.from(e.dataTransfer.items);
    const files: File[] = [];

    // Check if we have directory entries (folder drop)
    const entries = items
      .map((item) => item.webkitGetAsEntry?.())
      .filter((entry): entry is FileSystemEntry => entry !== null);

    if (entries.length > 0 && entries.some((e) => e.isDirectory)) {
      // Folder drop - read all files recursively
      setIsParsing(true);
      try {
        for (const entry of entries) {
          if (entry.isDirectory) {
            const dirFiles = await readDirectoryEntries(entry as FileSystemDirectoryEntry);
            files.push(...dirFiles);
          } else if (entry.isFile) {
            const file = await new Promise<File>((resolve, reject) => {
              (entry as FileSystemFileEntry).file(resolve, reject);
            });
            files.push(file);
          }
        }

        setSelectedFiles(files);
        const result = await parseFolderStructure(files);
        setFolderParseResult(result);

        if (result.conversations.length > 0) {
          // Auto-switch to folder mode if we got a folder
          setSelectedSource('folder');
          setCurrentStep('preview');
        } else {
          setError('No conversation files found in the dropped folder');
        }
      } catch (err) {
        setError('Failed to read folder contents');
      } finally {
        setIsParsing(false);
      }
    } else {
      // File drop
      const droppedFiles = Array.from(e.dataTransfer.files).filter((f) =>
        f.name.endsWith('.json')
      );
      if (droppedFiles.length > 0) {
        setSelectedFiles(droppedFiles);
        setError(null);
      } else {
        setError('Please drop JSON files or a folder containing conversations');
      }
    }
  }, []);

  // Process the import
  const handleImport = useCallback(async () => {
    if (selectedSource === 'url') {
      // URL import via chatgptImport
      if (!urlInput.trim()) {
        setError('Please enter a ChatGPT URL');
        return;
      }

      setCurrentStep('processing');
      setIsProcessing(true);
      setError(null);

      try {
        const result = await api.chatgptImport.fetchConversation(urlInput);
        setResults({
          success: true,
          total: 1,
          imported: 1,
          failed: 0,
          conversations: [{
            title: result.title,
            threadId: result.threadId,
            messageCount: result.nodeCount,
            stored: result.stored || result.nodeCount,
            skipped: result.skipped || 0,
          }],
        });
        setCurrentStep('results');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to import conversation';
        setError(message);
        setCurrentStep('upload');
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // File-based imports
    const filesToImport = selectedSource === 'folder' && folderParseResult
      ? folderParseResult.conversations.map((c) => c.file)
      : selectedFiles;

    if (filesToImport.length === 0) {
      setError('Please select files to import');
      return;
    }

    setCurrentStep('processing');
    setIsProcessing(true);
    setError(null);

    try {
      if (selectedSource === 'humanizer') {
        // Humanizer export format
        const result = await api.fileImport.uploadHumanizerExport(filesToImport[0]);
        setResults({
          success: result.success,
          total: result.total,
          imported: result.imported,
          failed: result.failed,
          conversations: [],
        });
      } else {
        // ChatGPT conversations (files or folder)
        if (filesToImport.length === 1) {
          const result = await api.fileImport.uploadConversation(filesToImport[0]);
          setResults({
            success: result.success,
            total: 1,
            imported: result.success ? 1 : 0,
            failed: result.success ? 0 : 1,
            conversations: [{
              title: result.title,
              threadId: result.threadId,
              messageCount: result.messageCount,
              stored: result.stored,
              skipped: result.skipped,
            }],
          });
        } else {
          const result = await api.fileImport.uploadConversations(filesToImport);
          setResults(result);
        }
      }
      setCurrentStep('results');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to import files';
      setError(message);
      setCurrentStep('upload');
    } finally {
      setIsProcessing(false);
    }
  }, [api, selectedSource, selectedFiles, urlInput, folderParseResult]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    switch (currentStep) {
      case 'upload':
        setCurrentStep('source');
        setSelectedSource(null);
        setSelectedFiles([]);
        setFolderParseResult(null);
        setUrlInput('');
        break;
      case 'preview':
        setCurrentStep('upload');
        setSelectedFiles([]);
        setFolderParseResult(null);
        break;
      case 'results':
        setCurrentStep('source');
        setSelectedSource(null);
        setSelectedFiles([]);
        setFolderParseResult(null);
        setResults(null);
        break;
      default:
        break;
    }
    setError(null);
  }, [currentStep]);

  // Handle import another
  const handleImportAnother = useCallback(() => {
    setCurrentStep('source');
    setSelectedSource(null);
    setSelectedFiles([]);
    setFolderParseResult(null);
    setResults(null);
    setError(null);
    setUrlInput('');
  }, []);

  // Format file size
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get current source config
  const sourceConfig = SOURCES.find((s) => s.id === selectedSource);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'source':
        return (
          <div className="import-sources">
            {SOURCES.map((source) => (
              <div
                key={source.id}
                className="import-source"
                role="button"
                tabIndex={0}
                onClick={() => handleSourceSelect(source.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSourceSelect(source.id);
                }}
              >
                <span className="import-source__icon">{source.icon}</span>
                <div className="import-source__info">
                  <h3 className="import-source__name">{source.name}</h3>
                  <p className="import-source__description">{source.description}</p>
                </div>
              </div>
            ))}
          </div>
        );

      case 'upload':
        if (selectedSource === 'url') {
          return (
            <div className="import-sources">
              <div className="import-source import-source--column">
                <div className="import-url-input">
                  <span className="import-url-input__icon">🔗</span>
                  <input
                    type="url"
                    className="import-url-input__field"
                    placeholder="https://chatgpt.com/c/..."
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleImport();
                    }}
                    aria-label="ChatGPT conversation URL"
                  />
                </div>
                <p className="import-url-hint">
                  Paste a ChatGPT conversation URL. You must be logged into ChatGPT
                  and have connected your token in Settings.
                </p>
              </div>
            </div>
          );
        }

        return (
          <div className="import-sources">
            <div
              className={`import-dropzone ${isDragging ? 'import-dropzone--dragging' : ''} ${selectedFiles.length > 0 ? 'import-dropzone--has-files' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => {
                if (sourceConfig?.allowFolder) {
                  folderInputRef.current?.click();
                } else {
                  fileInputRef.current?.click();
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (sourceConfig?.allowFolder) {
                    folderInputRef.current?.click();
                  } else {
                    fileInputRef.current?.click();
                  }
                }
              }}
            >
              {/* File input for individual files */}
              <input
                ref={fileInputRef}
                type="file"
                accept={sourceConfig?.acceptTypes || '.json'}
                multiple={selectedSource === 'chatgpt'}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              {/* Folder input */}
              <input
                ref={folderInputRef}
                type="file"
                // @ts-expect-error - webkitdirectory is not in HTMLInputElement types
                webkitdirectory=""
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              {isParsing ? (
                <>
                  <div className="panel__loading-spinner" />
                  <div className="import-dropzone__text">
                    <strong>Scanning folder...</strong>
                  </div>
                </>
              ) : selectedFiles.length === 0 ? (
                <>
                  <span className="import-dropzone__icon">
                    {sourceConfig?.allowFolder ? '📁' : '📄'}
                  </span>
                  <div className="import-dropzone__text">
                    <strong>
                      {sourceConfig?.allowFolder
                        ? 'Drop folder here'
                        : 'Drop files here'}
                    </strong>
                    <span>or click to browse</span>
                  </div>
                  <span className="import-dropzone__hint">
                    {selectedSource === 'folder'
                      ? 'Select a ChatGPT/Claude export folder'
                      : selectedSource === 'chatgpt'
                        ? 'Select one or more conversation JSON files'
                        : 'Select a Humanizer export JSON file'}
                  </span>
                </>
              ) : (
                <>
                  <span className="import-dropzone__icon">✓</span>
                  <div className="import-dropzone__text">
                    <strong>
                      {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                    </strong>
                  </div>
                  <ul className="import-dropzone__files">
                    {selectedFiles.slice(0, 5).map((file, i) => (
                      <li key={i}>{file.name}</li>
                    ))}
                    {selectedFiles.length > 5 && (
                      <li>... and {selectedFiles.length - 5} more</li>
                    )}
                  </ul>
                </>
              )}
            </div>
          </div>
        );

      case 'preview':
        if (!folderParseResult) return null;

        return (
          <div className="import-preview">
            <div className="import-preview__summary">
              <div className="import-preview__stat">
                <span className="import-preview__stat-value">
                  {folderParseResult.conversations.length}
                </span>
                <span className="import-preview__stat-label">Conversations</span>
              </div>
              <div className="import-preview__stat">
                <span className="import-preview__stat-value">
                  {folderParseResult.conversations.reduce((sum, c) => sum + c.messageCount, 0)}
                </span>
                <span className="import-preview__stat-label">Messages</span>
              </div>
              <div className="import-preview__stat">
                <span className="import-preview__stat-value">
                  {folderParseResult.mediaFiles.length}
                </span>
                <span className="import-preview__stat-label">Media Files</span>
              </div>
              <div className="import-preview__stat">
                <span className="import-preview__stat-value">
                  {formatSize(folderParseResult.totalSize)}
                </span>
                <span className="import-preview__stat-label">Total Size</span>
              </div>
            </div>

            <div className="import-preview__list">
              <h4>Conversations to import:</h4>
              {folderParseResult.conversations.slice(0, 10).map((conv, i) => (
                <div key={i} className="import-preview__item">
                  <span className="import-preview__item-title">{conv.title}</span>
                  <span className="import-preview__item-meta">
                    {conv.messageCount} messages • {conv.source}
                  </span>
                </div>
              ))}
              {folderParseResult.conversations.length > 10 && (
                <div className="import-preview__more">
                  ... and {folderParseResult.conversations.length - 10} more
                </div>
              )}
            </div>

            {folderParseResult.mediaFiles.length > 0 && (
              <p className="import-preview__note">
                Note: Media files will be imported with their conversations.
              </p>
            )}
          </div>
        );

      case 'processing':
        return (
          <div className="import-processing">
            <div className="panel__loading-spinner" />
            <div className="import-processing__text">
              Processing{' '}
              {selectedSource === 'url'
                ? 'URL'
                : selectedSource === 'folder' && folderParseResult
                  ? `${folderParseResult.conversations.length} conversation${folderParseResult.conversations.length > 1 ? 's' : ''}`
                  : `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}`}
              ...
            </div>
          </div>
        );

      case 'results':
        if (!results) return null;

        return (
          <div className="import-results">
            <div
              className={`import-results__summary ${results.success ? 'import-results__summary--success' : 'import-results__summary--partial'}`}
            >
              <span className="import-results__summary-icon">
                {results.failed === 0 ? '✓' : '⚠️'}
              </span>
              <div className="import-results__summary-text">
                <strong>
                  {results.failed === 0
                    ? 'Import Complete!'
                    : `Imported ${results.imported} of ${results.total}`}
                </strong>
                <span>
                  {results.imported} conversation{results.imported !== 1 ? 's' : ''} imported
                  {results.failed > 0 && `, ${results.failed} failed`}
                </span>
              </div>
            </div>

            {results.conversations.length > 0 && (
              <div className="import-results__list">
                {results.conversations.map((conv, i) => (
                  <div
                    key={i}
                    className={`import-results__item ${conv.error ? 'import-results__item--error' : ''}`}
                  >
                    <span className="import-results__item-icon">
                      {conv.error ? '✗' : '✓'}
                    </span>
                    <div className="import-results__item-info">
                      <strong>{conv.title || 'Untitled'}</strong>
                      {conv.error ? (
                        <span className="import-results__item-error">{conv.error}</span>
                      ) : (
                        <span>
                          {conv.messageCount} messages • {conv.stored} stored, {conv.skipped}{' '}
                          skipped
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`import-wizard ${className}`}>
      {/* Header */}
      <div className="import-wizard__header">
        <h2 className="import-wizard__title">
          {currentStep === 'source' && 'Import Conversations'}
          {currentStep === 'upload' && sourceConfig?.name}
          {currentStep === 'preview' && 'Preview Import'}
          {currentStep === 'processing' && 'Importing...'}
          {currentStep === 'results' && 'Import Complete'}
        </h2>
        {currentStep === 'upload' && sourceConfig && (
          <p className="import-wizard__subtitle">{sourceConfig.description}</p>
        )}
      </div>

      {/* Content */}
      <div className="import-wizard__content">
        {error && <div className="import-wizard__error">{error}</div>}
        {renderStepContent()}
      </div>

      {/* Footer with navigation */}
      <div className="import-wizard__footer">
        {currentStep !== 'source' && currentStep !== 'processing' && (
          <button
            className="import-wizard__btn import-wizard__btn--secondary"
            onClick={currentStep === 'results' ? handleImportAnother : handleBack}
            disabled={isProcessing || isParsing}
          >
            {currentStep === 'results' ? 'Import More' : '← Back'}
          </button>
        )}
        <div className="import-wizard__footer-spacer" />
        {currentStep === 'upload' && selectedSource !== 'folder' && (
          <button
            className="import-wizard__btn import-wizard__btn--primary"
            onClick={handleImport}
            disabled={selectedSource !== 'url' && selectedFiles.length === 0}
          >
            Import{' '}
            {selectedSource === 'url'
              ? 'URL'
              : `${selectedFiles.length || ''} File${selectedFiles.length !== 1 ? 's' : ''}`}{' '}
            →
          </button>
        )}
        {currentStep === 'preview' && (
          <button
            className="import-wizard__btn import-wizard__btn--primary"
            onClick={handleImport}
            disabled={!folderParseResult || folderParseResult.conversations.length === 0}
          >
            Import {folderParseResult?.conversations.length} Conversation
            {(folderParseResult?.conversations.length || 0) !== 1 ? 's' : ''} →
          </button>
        )}
        {currentStep === 'results' && (
          <button
            className="import-wizard__btn import-wizard__btn--primary"
            onClick={() => {
              onImportComplete?.();
            }}
          >
            Done
          </button>
        )}
      </div>
    </div>
  );
}

export default ImportView;
